export type ChallengeStatus = 'active' | 'paused' | 'completed';

export type ChallengeSettingsForm = {
  challengeName: string;
  challengeStatus: ChallengeStatus;
  startingBalance: string;
  targetGoal: string;
  brokerName: string;
  startDate: string;
  endDate: string;
  tradingStartTime: string;
  tradingEndTime: string;
  timeZone: string;
  youtubePlaylistUrl: string;
  autoSyncLikes: boolean;
  youtubeLikes: string;
  contributionPerLike: string;
  maximumContribution: string;
  youtubeLastSynced: string | null;
};

export type ChallengeSettingsErrors = Partial<Record<keyof ChallengeSettingsForm, string>>;

const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_INPUT_PATTERN = /^\d{2}:\d{2}$/;
const PLAYLIST_ID_PATTERN = /^[A-Za-z0-9_-]{10,}$/;

export function parseCurrencyInput(value: string): number {
  const normalized = String(value || '').replace(/[^0-9.-]/g, '');
  if (!normalized || normalized === '-' || normalized === '.' || normalized === '-.') return Number.NaN;
  return Number(normalized);
}

export function formatCurrencyForInput(value: number): string {
  if (!Number.isFinite(value)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatCurrencyWhileTyping(value: string): string {
  const stripped = String(value || '').replace(/[$,\s]/g, '').replace(/[^0-9.-]/g, '');
  if (!stripped) return '';

  const isNegative = stripped.startsWith('-');
  const unsigned = stripped.replace(/-/g, '');
  const [rawInteger = '', ...fractionParts] = unsigned.split('.');
  const integerDigits = rawInteger.replace(/^0+(?=\d)/, '') || '0';
  const groupedInteger = Number(integerDigits).toLocaleString('en-US', { maximumFractionDigits: 0 });
  const hadDecimal = unsigned.includes('.');
  const fraction = fractionParts.join('').slice(0, 2);

  return `${isNegative ? '-' : ''}$${groupedInteger}${hadDecimal ? `.${fraction}` : ''}`;
}

export function parseYouTubePlaylistId(value: string): string | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  if (PLAYLIST_ID_PATTERN.test(raw) && !raw.includes('.')) return raw;

  try {
    const parsed = new URL(raw);
    const hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');
    if (!['youtube.com', 'm.youtube.com', 'music.youtube.com'].includes(hostname)) return null;
    const playlistId = String(parsed.searchParams.get('list') || '').trim();
    return PLAYLIST_ID_PATTERN.test(playlistId) ? playlistId : null;
  } catch {
    return null;
  }
}

export function formatChallengeDateRange(startDate: string, endDate: string): string {
  if (!DATE_INPUT_PATTERN.test(startDate) || !DATE_INPUT_PATTERN.test(endDate)) return 'Dates not set';
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return 'Dates not set';

  const shortDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const fullDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const sameMonthAndYear = start.getUTCFullYear() === end.getUTCFullYear() && start.getUTCMonth() === end.getUTCMonth();
  if (sameMonthAndYear) {
    return `${shortDate.format(start)} – ${end.getUTCDate()}, ${end.getUTCFullYear()}`;
  }

  return `${fullDate.format(start)} – ${fullDate.format(end)}`;
}

export function formatChallengeChartDate(value: string): string {
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(parsed);
}

export function formatChallengeStartDate(startDate: string): string {
  if (!DATE_INPUT_PATTERN.test(startDate)) return 'Start';
  const parsed = new Date(`${startDate}T00:00:00Z`);
  if (!Number.isFinite(parsed.getTime())) return 'Start';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

export function validateChallengeSettings(form: ChallengeSettingsForm): ChallengeSettingsErrors {
  const errors: ChallengeSettingsErrors = {};
  const startingBalance = parseCurrencyInput(form.startingBalance);
  const targetGoal = parseCurrencyInput(form.targetGoal);
  const youtubeLikes = Number(form.youtubeLikes);
  const contributionPerLike = parseCurrencyInput(form.contributionPerLike);
  const maximumContribution = parseCurrencyInput(form.maximumContribution);

  if (!form.challengeName.trim()) errors.challengeName = 'Challenge name is required.';
  if (!Number.isFinite(startingBalance) || startingBalance <= 0) errors.startingBalance = 'Starting capital must be greater than 0.';
  if (!Number.isFinite(targetGoal) || targetGoal <= 0) {
    errors.targetGoal = 'Challenge goal must be greater than 0.';
  } else if (Number.isFinite(startingBalance) && startingBalance > 0 && targetGoal <= startingBalance) {
    errors.targetGoal = 'Challenge goal must be greater than starting capital.';
  }
  if (!form.brokerName.trim()) errors.brokerName = 'Broker name is required.';

  if (!DATE_INPUT_PATTERN.test(form.startDate)) errors.startDate = 'Start date is required.';
  if (!DATE_INPUT_PATTERN.test(form.endDate)) {
    errors.endDate = 'End date is required.';
  } else if (DATE_INPUT_PATTERN.test(form.startDate) && form.endDate < form.startDate) {
    errors.endDate = 'End date cannot be earlier than start date.';
  }

  if (!TIME_INPUT_PATTERN.test(form.tradingStartTime)) errors.tradingStartTime = 'Start time is required.';
  if (!TIME_INPUT_PATTERN.test(form.tradingEndTime)) {
    errors.tradingEndTime = 'End time is required.';
  } else if (TIME_INPUT_PATTERN.test(form.tradingStartTime) && form.tradingEndTime <= form.tradingStartTime) {
    errors.tradingEndTime = 'Trading end time must be later than start time.';
  }
  if (!form.timeZone) errors.timeZone = 'Time zone is required.';

  if (form.youtubePlaylistUrl && !parseYouTubePlaylistId(form.youtubePlaylistUrl)) {
    errors.youtubePlaylistUrl = 'Enter a valid YouTube playlist URL.';
  } else if (form.autoSyncLikes && !form.youtubePlaylistUrl) {
    errors.youtubePlaylistUrl = 'A playlist URL is required for automatic sync.';
  }
  if (!form.autoSyncLikes && (!Number.isFinite(youtubeLikes) || youtubeLikes < 0)) {
    errors.youtubeLikes = 'YouTube total likes cannot be negative.';
  }
  if (!Number.isFinite(contributionPerLike) || contributionPerLike < 0) {
    errors.contributionPerLike = 'Contribution per like cannot be negative.';
  }
  if (!Number.isFinite(maximumContribution) || maximumContribution < 0) {
    errors.maximumContribution = 'Maximum contribution cannot be negative.';
  }

  return errors;
}
